import { useEffect, useState } from "react";
import HeadingTitle from "./utils/HeadingTitle";
import {
  FaCode,
  FaDatabase,
  FaGlobe,
  FaTools,
  FaCogs,
  FaLanguage,
  FaJava,
  FaPython,
  FaCuttlefish, // fallback for C#
  FaJs,
  FaGitAlt,
  FaMicrochip, // assembly
} from "react-icons/fa";
import {
  SiCplusplus,
  SiTypescript,
  SiKotlin,
  SiGnubash,
  SiReact,
  SiNodedotjs,
  SiSpringboot,
  SiJunit5,
  SiJest,
  SiMongodb,
  SiGooglecloud,
  SiSwagger,
  SiIntellijidea,
  SiUml,
  SiGo,
  SiFlutter,
} from "react-icons/si";
import { VscVscode } from "react-icons/vsc";
import { useInView } from "react-intersection-observer";
import skillCategories from "../data/skills";

const SkillCard = ({ skillIcons, category, skillList }) => {
  const { ref: skillRef, inView: skillIsVisible } = useInView();
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const categoryAccents = {
    "Programming Languages": "bg-gradient-to-r from-sky-500/60 to-cyan-500/50",
    "Frameworks & Libraries": "bg-gradient-to-r from-emerald-500/50 to-teal-500/45",
    "Databases": "bg-gradient-to-r from-indigo-500/50 to-blue-500/45",
    "Tools & Platforms": "bg-gradient-to-r from-amber-500/50 to-orange-500/45",
    "Additional Knowledge": "bg-gradient-to-r from-violet-500/50 to-fuchsia-500/45",
    "Languages": "bg-gradient-to-r from-rose-500/50 to-pink-500/45",
  };
  const categoryStyle = {
    accent: categoryAccents[category] || categoryAccents["Frameworks & Libraries"],
    pattern:
      "bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.26)_1px,transparent_0)] bg-[size:14px_14px]",
  };

  useEffect(() => {
    if (skillIsVisible) {
      setHasBeenVisible(true);
    }
  }, [skillIsVisible]);

  return (
    <div
      ref={skillRef}
      className={`group fade-in-section relative overflow-hidden w-full max-w-[380px] h-full bg-slate-50 dark:bg-slate-900/40 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/40 dark:shadow-none hover:border-primary/40 hover:scale-[1.03] hover:shadow-[0_14px_34px_rgb(var(--color-primary-rgb)/0.22)] dark:hover:shadow-[0_14px_34px_rgb(var(--color-primary-rgb)/0.28)] transition-all duration-300 ${hasBeenVisible ? "is-visible" : ""
        }`}
    >
      <div className={`absolute inset-0 opacity-40 dark:opacity-20 ${categoryStyle.pattern}`} />
      <div className={`absolute left-0 top-0 h-1 w-full ${categoryStyle.accent}`} />

      <div className="relative z-10 h-full">
        <h3 className="flex items-center mb-4 text-xl font-semibold">
          {skillIcons[category]}
          <span className="ml-2">{category}</span>
        </h3>
        <div className="flex flex-wrap gap-3">
          {skillList.map(({ name, icon }) => (
            <div
              key={name}
              className="flex w-[90px] flex-col items-center text-center rounded-xl border border-slate-200/80 bg-white/65 px-2 py-3 text-slate-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 dark:border-slate-700/70 dark:bg-slate-900/35 dark:text-slate-200 dark:hover:border-slate-600"
            >
              <div className="text-2xl">{icon}</div>
              <small className="mt-2 text-[11px] font-semibold leading-tight">{name}</small>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Skills = () => {
  const skillItemIcons = {
    java: <FaJava />,
    python: <FaPython />,
    cplusplus: <SiCplusplus />,
    csharp: <FaCuttlefish />,
    javascript: <FaJs />,
    typescript: <SiTypescript />,
    kotlin: <SiKotlin />,
    bash: <SiGnubash />,
    go: <SiGo />,
    react: <SiReact />,
    nodejs: <SiNodedotjs />,
    springboot: <SiSpringboot />,
    flutter: <SiFlutter />,
    junit: <SiJunit5 />,
    jest: <SiJest />,
    database: <FaDatabase />,
    mongodb: <SiMongodb />,
    googlecloud: <SiGooglecloud />,
    git: <FaGitAlt />,
    swagger: <SiSwagger />,
    vscode: <VscVscode />,
    intellij: <SiIntellijidea />,
    uml: <SiUml />,
    assembly: <FaMicrochip />,
    cogs: <FaCogs />,
    language: <FaLanguage />,
  };

  const skills = skillCategories.map(({ category, skills: categorySkills }) => ({
    category,
    skillList: categorySkills.map(({ name, iconKey }) => ({
      name,
      icon: skillItemIcons[iconKey] || <FaCode />,
    })),
  }));

  const skillIcons = {
    "Programming Languages": <FaCode className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
    "Frameworks & Libraries": <FaGlobe className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
    Databases: <FaDatabase className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
    "Tools & Platforms": <FaTools className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
    "Additional Knowledge": <FaCogs className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
    Languages: <FaLanguage className="hoverIcon text-primary transition-transform duration-[1500ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(360deg)]" />,
  };

  return (
    <div className="px-5 py-24" id="skills">
      <HeadingTitle title="Skills" description="Tools and technologies I've mastered over the years." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 max-w-[1280px] mx-auto justify-items-center">
        {skills.map(({ category, skillList }) => (
          <div key={category} className="w-full flex justify-center">
            <SkillCard
              skillIcons={skillIcons}
              category={category}
              skillList={skillList}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Skills;
